// Call-analytics service: reconciled KPI + aggregation fetchers + xlsx export.
// All calls share one filter object; arrays are serialized as repeated params
// (indexes: null) so Flask's request.args.getlist reads them directly.
import apiClient from './apiClient';

// No call data exists before this date → the date pickers can't go earlier.
export const MIN_DATE = '2026-08-01';

export type Granularity = 'day' | 'week' | 'month';
export type BreakdownDimension = 'intento' | 'macro_categoria' | 'servizio';

export interface AnalyticsFilters {
  period?: string;               // 'sempre' | 'oggi' | 'ieri' | 'ultimi_7_giorni' | 'questo_mese'
  from?: string;                 // YYYY-MM-DD (overrides period)
  to?: string;
  intento?: string[];
  macro_categoria?: string[];
  servizio?: string[];
  trasferita?: '' | '0' | '1';
  vuole_prenotare?: '' | '0' | '1';
  sat_min?: number | '';
  sat_max?: number | '';
  dur_min?: number | '';
  dur_max?: number | '';
  // Dedicated too-brief filter. Not expressible through dur_min/dur_max: those
  // drop the calls with no recorded duration, which the shared definition includes.
  brevi?: '' | '1';
}

export interface KpiSummary {
  operativi: {
    chiamate: number;
    troppo_brevi: number; troppo_brevi_pct: number;
    gestite: number; gestite_pct: number;
    trasferite: number; trasferite_pct: number;
    autonome: number; autonome_pct: number;
    soddisfazione_media: number | null;
  };
  // richieste = intent (vuole_prenotare, from the LLM); completate / non_effettuate
  // = the comune booking API's recorded outcome. Two sources on purpose.
  prenotazioni: { richieste: number; completate: number; non_effettuate: number };
  costi: { complessivo_eur: number };
}

export interface VolumePoint { bucket: string; count: number }
export interface WeekdayPoint { weekday: number; label: string; count: number }
export interface HeatmapCell { weekday: number; hour: number; count: number }
export interface BreakdownItem { value: string; count: number }
export interface DurationBucket { label: string; count: number }

function toParams(f: AnalyticsFilters): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (f.from) p.from = f.from;
  if (f.to) p.to = f.to;
  if (!f.from && !f.to && f.period && f.period !== 'sempre') p.period = f.period;
  (['intento', 'macro_categoria', 'servizio'] as const).forEach(k => {
    if (f[k] && f[k]!.length) p[k] = f[k];
  });
  if (f.brevi === '1') p.brevi = '1';
  if (f.trasferita === '0' || f.trasferita === '1') p.trasferita = f.trasferita;
  if (f.vuole_prenotare === '0' || f.vuole_prenotare === '1') p.vuole_prenotare = f.vuole_prenotare;
  (['sat_min', 'sat_max', 'dur_min', 'dur_max'] as const).forEach(k => {
    const v = f[k];
    if (v !== '' && v !== undefined && v !== null) p[k] = v;
  });
  return p;
}

function get<T>(url: string, f: AnalyticsFilters, extra: Record<string, unknown> = {}): Promise<T> {
  return apiClient
    .get<T>(url, { params: { ...toParams(f), ...extra }, paramsSerializer: { indexes: null } })
    .then(r => r.data);
}

export const getKpi = (f: AnalyticsFilters) => get<KpiSummary>('/api/calls/analytics/kpi', f);
export const getVolume = (f: AnalyticsFilters, granularity: Granularity) =>
  get<VolumePoint[]>('/api/calls/analytics/volume', f, { granularity });
export const getWeekday = (f: AnalyticsFilters) =>
  get<WeekdayPoint[]>('/api/calls/analytics/weekday', f);
export const getHeatmap = (f: AnalyticsFilters) =>
  get<HeatmapCell[]>('/api/calls/analytics/heatmap', f);
export const getBreakdown = (f: AnalyticsFilters, dimension: BreakdownDimension) =>
  get<BreakdownItem[]>('/api/calls/analytics/breakdown', f, { dimension });
export const getDuration = (f: AnalyticsFilters) =>
  get<DurationBucket[]>('/api/calls/analytics/duration', f);

// Aggregated export: the figures behind the active report, no per-call rows.
// `view` travels from here because the report names and the section vocabulary
// live in the frontend — the server recomputing "what is visible" would be a
// second definition of it, and two definitions drift. Title and period go the
// same way, as display text for the header sheet.
export async function downloadReportExport(
  f: AnalyticsFilters, view: ViewKey[], granularity: Granularity,
  title: string, periodo: string,
  // Title and sentence per chart, keyed as the server keys its sheets (kpi,
  // volume, motivo, ufficio, servizio, weekday, durata, heatmap). Sent as
  // title_<key> / desc_<key>.
  texts: Record<string, { title: string; description: string }>,
): Promise<void> {
  const desc = Object.fromEntries(Object.entries(texts).flatMap(([k, t]) => [
    [`title_${k}`, t.title], [`desc_${k}`, t.description],
  ]));
  const res = await apiClient.get('/api/calls/analytics/export', {
    params: { ...toParams(f), view, granularity, title, periodo, ...desc },
    paramsSerializer: { indexes: null },
    responseType: 'blob',
  });
  saveBlob(res.data, filenameFrom(res.headers, 'report.xlsx'));
}

// Content-Disposition carries the server-built name (report + period); fall back
// only if it is missing, so the file is not left anonymous.
function filenameFrom(headers: unknown, fallback: string): string {
  const cd = (headers as Record<string, string> | undefined)?.['content-disposition'];
  const m = cd?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return m ? decodeURIComponent(m[1]) : fallback;
}

function saveBlob(data: BlobPart, filename: string): void {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadExport(f: AnalyticsFilters): Promise<void> {
  const res = await apiClient.get('/api/calls/export', {
    params: { ...toParams(f), format: 'xlsx' },
    paramsSerializer: { indexes: null },
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'chiamate.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// -- Presets (per-comune; admin creates/deletes, everyone applies) ----------
// A preset carries the filters + a view (which chart sections to show).
// Both live inside the opaque `filters` blob as { filters, view }; the backend
// never interprets it. The time window is stored too; a preset saved without
// one opens with no dates, and so with no charts until dates are chosen.
// 'motivo' = only the L1 chart; 'categorie' = L2+L3 only. Kept separate so
// "Motivo di contatto" (L1, no date limit) and "…categoria e servizio" (L2/L3,
// MIN_DATE limit) are distinct presets. ALL_VIEW has both → full render.
export type ViewKey = 'kpi' | 'volume' | 'motivo' | 'categorie' | 'distribuzioni';
export const ALL_VIEW: ViewKey[] = ['kpi', 'volume', 'motivo', 'categorie', 'distribuzioni'];

export interface PresetContent {
  filters: AnalyticsFilters;   // period/from/to included
  view: ViewKey[];
  // Optional default bucket size for the volume chart. Its own field rather than
  // something inferred from `view`, so the two stay independently choosable.
  // Absent = leave the current granularity alone, which is also what every
  // preset stored before this field existed means (no blob migration needed).
  granularity?: Granularity;
  // Per-chart title/description overrides, keyed by chart (kpi, volume, motivo,
  // ufficio, servizio, weekday, durata, heatmap). Absent = the default texts,
  // which is also what every preset stored before this field existed means.
  texts?: ChartTexts;
  // What the report is for, written by the admin who saved it. Shown under its
  // name in the report list, where the choice is made.
  description?: string;
  // The saved dates are fixed for users: the date fields are read-only for them.
  lockDates?: boolean;
}

export const REPORT_DESCRIPTION_MAX = 300;

export interface ChartText { title?: string; description?: string }
export type ChartTexts = Partial<Record<string, ChartText>>;

export interface FilterPreset {
  id: number | string;         // string id = built-in (not deletable)
  name: string;
  content: PresetContent;
  updated_at?: string;
  builtin?: boolean;
}

interface PresetRow { id: number; name: string; filters: unknown; updated_at?: string }

// The stored blob is written by whoever saved the report and never checked by the
// server, so it is read defensively: only string values, only known keys, capped
// to the editor's limits. A malformed entry is dropped rather than rendered.
const TEXT_KEYS = ['kpi', 'volume', 'motivo', 'ufficio', 'servizio', 'weekday', 'durata', 'heatmap'];
function readTexts(raw: unknown): ChartTexts | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: ChartTexts = {};
  for (const key of TEXT_KEYS) {
    const t = (raw as Record<string, unknown>)[key];
    if (!t || typeof t !== 'object') continue;
    const { title, description } = t as Record<string, unknown>;
    const entry: ChartText = {};
    if (typeof title === 'string' && title.trim()) entry.title = title.slice(0, 120);
    if (typeof description === 'string') entry.description = description.slice(0, 400);
    if (Object.keys(entry).length) out[key] = entry;
  }
  return Object.keys(out).length ? out : undefined;
}

export function toContent(blob: unknown): PresetContent {
  const b = blob as {
    filters?: AnalyticsFilters; view?: ViewKey[]; granularity?: Granularity; texts?: unknown;
    lockDates?: unknown;
  } | null;
  if (b && Array.isArray(b.view) && b.filters) {
    const description = typeof (b as { description?: unknown }).description === 'string'
      ? ((b as { description: string }).description).trim().slice(0, REPORT_DESCRIPTION_MAX) || undefined
      : undefined;
    return {
      filters: b.filters, view: b.view, granularity: b.granularity,
      texts: readTexts(b.texts), description, lockDates: b.lockDates === true || undefined,
    };
  }
  // legacy / unknown shape → treat the whole blob as filters, show all sections
  return { filters: (b && typeof b === 'object' ? (b as AnalyticsFilters) : {}), view: ALL_VIEW };
}

export const getPresets = (): Promise<FilterPreset[]> =>
  apiClient.get<PresetRow[]>('/api/calls/analytics/presets').then(r =>
    r.data.map(row => ({
      id: row.id, name: row.name, content: toContent(row.filters), updated_at: row.updated_at,
    })));

export const savePreset = (name: string, content: PresetContent) =>
  apiClient.post('/api/calls/analytics/presets', { name, filters: content }).then(r => r.data);

export const deletePreset = (id: number) =>
  apiClient.delete(`/api/calls/analytics/presets/${id}`).then(r => r.data);
